/* Strike Gently theme.js — vanilla, no dependencies.
   Cart (Ajax API + Section Rendering), cart drawer, quick add, header/menu/search,
   predictive search, announcement marquee, promo/progress bars, bonus-item rules, tabs, misc.
   Add ?sgdebug to the URL to log swallowed errors to the console. */
(function () {
  'use strict';
  const SG = (window.SG = window.SG || {});
  SG.routes = SG.routes || {};
  SG.strings = SG.strings || {};
  const $ = (sel, root) => (root || document).querySelector(sel);
  const $$ = (sel, root) => Array.from((root || document).querySelectorAll(sel));
  const on = (el, ev, fn, opts) => el && el.addEventListener(ev, fn, opts);
  const debounce = (fn, ms) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; };
  SG.debug = SG.debug || /[?&]sgdebug/.test(window.location.search);
  const warn = (...a) => { if (SG.debug && window.console) console.warn('[SG]', ...a); };
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isPhone = () => window.innerWidth <= 800;
  const cssEscape = (s) => (window.CSS && CSS.escape ? CSS.escape(s) : String(s).replace(/["\\]/g, '\\$&'));
  SG.dom = { $, $$, on, debounce, warn, reduceMotion };

  /* ---------- Layers: one Escape handler closes the top-most open thing ---------- */
  const Layers = {
    stack: [],
    push(close) { this.remove(close); this.stack.push(close); },
    remove(close) { this.stack = this.stack.filter((c) => c !== close); },
    closeTop() { const close = this.stack.pop(); if (!close) return false; close(); return true; },
  };
  SG.layers = Layers;
  on(document, 'keydown', (e) => { if (e.key === 'Escape' && Layers.closeTop()) e.preventDefault(); });

  /* Make everything except `keep` inert while a modal layer is open (reference-counted). */
  function setInert(keep, active) {
    $$('body > *').forEach((node) => {
      if (node.matches('script, style, link, template')) return;
      if (keep.some((k) => k && (node === k || node.contains(k)))) return;
      const n = Math.max(0, (Number(node.dataset.sgInert) || 0) + (active ? 1 : -1));
      if (n) node.dataset.sgInert = String(n); else delete node.dataset.sgInert;
      node.inert = n > 0;
    });
  }
  SG.setInert = setInert;

  /* ---------- money ---------- */
  function formatMoney(cents) {
    const fmt = SG.money || '${{amount}}';
    const value = (Number(cents) || 0) / 100;
    let out;
    if (/amount_no_decimals_with_comma_separator/.test(fmt)) out = Math.round(value).toLocaleString('de-DE');
    else if (/amount_no_decimals/.test(fmt)) out = Math.round(value).toLocaleString('en-US');
    else if (/amount_with_comma_separator/.test(fmt)) out = value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    else out = value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return fmt.replace(/<[^>]*>/g, '').replace(/\{\{\s*amount[a-z_]*\s*\}\}/, out);
  }
  SG.formatMoney = formatMoney;

  /* ---------- toast ---------- */
  let toastTimer;
  function toast(msg, opts) {
    const el = $('#Toast');
    if (!el || !msg) return;
    opts = opts || {};
    el.textContent = '';
    const text = document.createElement('span');
    text.className = 'toast__text';
    text.textContent = msg;
    el.appendChild(text);
    if (opts.actionLabel && opts.onAction) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'toast__action';
      btn.textContent = opts.actionLabel;
      btn.addEventListener('click', () => { el.classList.remove('is-visible'); opts.onAction(); });
      el.appendChild(btn);
    }
    el.classList.toggle('toast--action', !!opts.actionLabel);
    el.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('is-visible'), opts.duration || 2600);
  }
  SG.toast = toast;
  const viewCartToast = (msg, duration) => toast(msg, { actionLabel: SG.strings.viewCart, onAction: () => CartDrawer.open(), duration: duration || 4200 });

  /* ---------- Cart API ---------- */
  const Cart = {
    state: null,
    seq: 0,
    async fetchJson(url, opts) {
      const res = await fetch(url, Object.assign({ headers: { 'Content-Type': 'application/json', Accept: 'application/json' } }, opts || {}));
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { const err = new Error(data.description || data.message || SG.strings.error); err.data = data; throw err; }
      return data;
    },
    async get() {
      this.state = await this.fetchJson(SG.routes.cart + '.js', { cache: 'no-store' });
      return this.state;
    },
    async add(items) {
      const data = await this.fetchJson(SG.routes.cartAdd + '.js', { method: 'POST', body: JSON.stringify({ items }) });
      await this.refresh();
      return data;
    },
    async change(payload) {
      this.state = await this.fetchJson(SG.routes.cartChange + '.js', { method: 'POST', body: JSON.stringify(payload) });
      await this.refresh(true);
      return this.state;
    },
    async update(payload) {
      this.state = await this.fetchJson(SG.routes.cartUpdate + '.js', { method: 'POST', body: JSON.stringify(payload) });
      await this.refresh(true);
      return this.state;
    },
    /* Re-render the drawer (and the cart page) from the Section Rendering API. Overlapping calls are
       sequenced: only the newest one touches the DOM. A failed cart.js after a successful add is not
       an error for the shopper — the sections still refresh from the current cart. */
    async refresh(haveState) {
      const seq = ++this.seq;
      const mainCart = $('#MainCart');
      const sectionIds = ['cart-drawer'];
      if (mainCart) sectionIds.push(mainCart.dataset.sectionId);
      // JSON-template sections (the cart page) only render in the context of their own URL
      const base = mainCart ? window.location.pathname : SG.routes.root;
      const [cartRes, sectionsRes] = await Promise.allSettled([
        haveState && this.state ? Promise.resolve(this.state) : this.get(),
        fetch(base + '?sections=' + sectionIds.join(','), { cache: 'no-store' }).then((r) => (r.ok ? r.json() : {})),
      ]);
      if (seq !== this.seq) return this.state; // superseded by a newer refresh
      if (cartRes.status === 'rejected') warn('cart.js', cartRes.reason);
      if (sectionsRes.status === 'rejected') warn('sections', sectionsRes.reason);
      const cart = cartRes.status === 'fulfilled' ? cartRes.value : this.state;
      if (!cart) throw cartRes.reason || new Error(SG.strings.error);
      const sections = (sectionsRes.status === 'fulfilled' && sectionsRes.value) || {};
      this.state = cart;
      if (sections['cart-drawer']) CartDrawer.render(sections['cart-drawer']);
      if (mainCart && sections[mainCart.dataset.sectionId]) {
        const doc = new DOMParser().parseFromString(sections[mainCart.dataset.sectionId], 'text/html');
        const fresh = doc.querySelector('#MainCart');
        if (fresh) {
          const note = $('textarea[name="note"]', mainCart); // keep what's being typed (its save may still be in flight)
          const typed = note ? note.value : null;
          mainCart.innerHTML = fresh.innerHTML;
          const n = typed !== null && $('textarea[name="note"]', mainCart);
          if (n) n.value = typed;
        }
      }
      this.updateCounts(cart);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
      return cart;
    },
    updateCounts(cart) {
      $$('[data-cart-count]').forEach((el) => {
        const changed = String(el.dataset.count) !== String(cart.item_count);
        el.textContent = cart.item_count; el.dataset.count = cart.item_count;
        if (changed && !reduceMotion()) { el.classList.remove('is-bumped'); void el.offsetWidth; el.classList.add('is-bumped'); }
      });
      $$('[data-cart-total], [data-cart-subtotal]').forEach((el) => { el.textContent = formatMoney(cart.total_price); });
    },
  };
  SG.cart = Cart;

  /* ---------- Cart drawer ---------- */
  const CartDrawer = {
    el: null,
    lastFocus: null,
    init() {
      this.el = $('#CartDrawer');
      if (!this.el) return;
      this._close = () => this.close();
      on(document, 'click', (e) => {
        const opener = e.target.closest('[data-cart-open]');
        // opened by hand (cart icon, menu): re-read the cart too, in case an app (upsell, rewards) changed it
        if (opener) { e.preventDefault(); this.open(); Cart.refresh().catch((err) => warn('cart', err)); return; }
        if (e.target.closest('[data-cart-close]')) { e.preventDefault(); this.close(); }
      });
      this.bind();
      window.openCartDrawer = () => this.open();
      window.closeCartDrawer = () => this.close();
    },
    bind() {
      const body = this.el;
      on(body, 'click', async (e) => {
        const qtyBtn = e.target.closest('[data-qty-change]');
        const remove = e.target.closest('[data-remove]');
        const item = e.target.closest('.cart-item');
        if (!item || (!qtyBtn && !remove)) return;
        e.preventDefault();
        if (qtyBtn && item.classList.contains('cart-item--bonus')) return; // free items: only removable
        const input = item.querySelector('[data-qty-input]');
        const qty = remove ? 0 : Math.max(0, (parseInt(input.value, 10) || 0) + parseInt(qtyBtn.dataset.qtyChange, 10));
        await this.setQuantity(item.dataset.key, qty);
      });
      on(body, 'change', async (e) => {
        const input = e.target.closest('[data-qty-input]');
        if (input) {
          const item = input.closest('.cart-item');
          if (item.classList.contains('cart-item--bonus')) { input.value = input.defaultValue; return; }
          const qty = typedQuantity(input);
          if (qty !== null) await this.setQuantity(item.dataset.key, qty);
          return;
        }
        if (e.target.matches('[data-cart-note]')) {
          try { await Cart.fetchJson(SG.routes.cartUpdate + '.js', { method: 'POST', body: JSON.stringify({ note: e.target.value }) }); } catch (err) { toast(err.message); }
        }
      });
    },
    async setQuantity(key, qty) {
      if (!key) return;
      this.el.setAttribute('aria-busy', 'true');
      try { await Cart.change({ id: key, quantity: qty }); }
      catch (err) { toast(err.message); }
      finally { this.el.removeAttribute('aria-busy'); }
    },
    section() { return (this.el && this.el.closest('.shopify-section')) || this.el; },
    render(html) {
      if (!this.el) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('#CartDrawer');
      if (!fresh) return;
      // keep keyboard focus on the same control after the swap
      const active = document.activeElement;
      const hadFocus = !!(active && this.el.contains(active));
      const row = hadFocus ? active.closest('.cart-item') : null;
      const key = row && row.dataset.key;
      const sel = key ? (active.matches('[data-qty-change]') ? '[data-qty-change="' + active.dataset.qtyChange + '"]' : active.matches('[data-remove]') ? '[data-remove]' : '[data-qty-input]') : null;
      PromoRotator.clear(this.el);
      // keep a note that's mid-typing (and its open panel) when free items re-render the drawer
      const note = $('[data-cart-note]', this.el);
      const typed = note ? note.value : null;
      const noteOpen = !!(note && note.closest('details') && note.closest('details').open);
      const noteFocused = !!(note && active === note);
      this.el.innerHTML = fresh.innerHTML;
      this.el.dataset.itemCount = fresh.dataset.itemCount;
      const n = $('[data-cart-note]', this.el);
      if (n && typed !== null) {
        if (n.value !== typed) n.value = typed;
        if (noteOpen && n.closest('details')) n.closest('details').open = true;
      }
      PromoRotator.init(this.el);
      if (!this.isOpen()) return;
      if (noteFocused && n) n.focus({ preventScroll: true });
      else if (hadFocus) {
        const r = key && this.el.querySelector('.cart-item[data-key="' + cssEscape(key) + '"]');
        const target = (r && r.querySelector(sel)) || $('.cart-drawer__close', this.el);
        if (target) target.focus({ preventScroll: true });
      }
      Recommendations.cart();
    },
    isOpen() { return !!(this.el && this.el.classList.contains('is-open')); },
    open() {
      if (!this.el) { window.location.href = SG.routes.cart; return; }
      if (this.isOpen()) return;
      if (document.body.classList.contains('menu-open') && SG.header) SG.header.setMenu(false);
      const openToast = $('#Toast.is-visible');
      if (openToast) { clearTimeout(toastTimer); openToast.classList.remove('is-visible'); } // "View cart" is moot once the cart is open, and it sat over Checkout
      this.lastFocus = document.activeElement;
      const nav = $('#SiteNav');
      if (this.lastFocus && nav && nav.contains(this.lastFocus) && isPhone()) this.lastFocus = $('.site-bar__icons [data-cart-open]');
      this.el.classList.add('is-open');
      this.el.setAttribute('aria-hidden', 'false');
      document.body.classList.add('drawer-open');
      setInert([this.section(), $('#Toast')], true);
      Layers.push(this._close);
      const panel = $('.cart-drawer__panel', this.el);
      setTimeout(() => { const first = $('.cart-drawer__close', this.el); (first || panel).focus({ preventScroll: true }); }, 0);
      Recommendations.cart();
    },
    close() {
      if (!this.el || !this.isOpen()) return;
      this.el.classList.remove('is-open');
      this.el.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('drawer-open');
      setInert([this.section(), $('#Toast')], false);
      Layers.remove(this._close);
      if (this.lastFocus && this.lastFocus.focus && document.contains(this.lastFocus)) this.lastFocus.focus({ preventScroll: true });
      this.lastFocus = null;
    },
  };
  SG.drawer = CartDrawer;

  /* ---------- Add to cart (forms + quick add) ---------- */
  function klaviyoAdded(item) {
    try {
      window._learnq = window._learnq || [];
      window._learnq.push(['track', 'Added to Cart', {
        Name: item.product_title, ProductID: item.product_id, Categories: [], ImageURL: item.image,
        URL: window.location.origin + item.url, Brand: item.vendor, Price: formatMoney(item.final_price || item.price),
      }]);
    } catch (e) { warn('klaviyo', e); }
  }
  SG.klaviyoAdded = klaviyoAdded;

  /* Text for the "keep shopping" toast: the matching tier bar's current line, e.g.
     "add 2 more pins · get 2 free random mystery pins". Nothing when that bar is the collection
     strip pinned to the bottom of the phone screen, which sits right under the toast and already says it. */
  function tierHint(added) {
    if (!added || !added.product_type) return '';
    const bar = $$('[data-promo][data-target-type="product_type"]').find((b) => b.dataset.target === added.product_type && (b.dataset.state === 'progressing' || b.dataset.state === 'completed'));
    if (!bar) return '';
    const strip = bar.closest('.collection-strip');
    if (strip && getComputedStyle(strip).position === 'fixed') return '';
    const t = $('[data-promo-text]', bar);
    if (!t) return '';
    const copy = t.cloneNode(true);
    $$('br', copy).forEach((br) => br.replaceWith(' · '));
    return copy.textContent.replace(/\s+/g, ' ').replace(/[.!]?\s*·\s*/g, ' · ').trim();
  }

  async function addToCart(items, button, opts) {
    opts = opts || {};
    if (button && button.getAttribute('aria-busy') === 'true') return null; // a request is already in flight
    const isMain = !!(button && button.matches('.button'));
    const textEl = button ? (button.querySelector('[data-add-to-cart-text]') || button) : null;
    const label = textEl ? textEl.textContent : '';
    if (button) { button.setAttribute('aria-busy', 'true'); if (isMain) textEl.textContent = SG.strings.adding; }
    try {
      const data = await Cart.add(items);
      const added = data.items ? data.items[0] : data;
      if (added) klaviyoAdded(added);
      const fromGrid = opts.source === 'grid';
      if (fromGrid && SG.quickAddAction === 'toast' && !CartDrawer.isOpen()) {
        const hint = tierHint(added);
        viewCartToast(hint ? SG.strings.added + ' · ' + hint : SG.strings.added);
      } else if (fromGrid && SG.quickAddAction === 'drawer') CartDrawer.open();
      else if (SG.autoOpenDrawer !== false) CartDrawer.open();
      else viewCartToast(SG.strings.added);
      document.dispatchEvent(new CustomEvent('cart:added', { detail: { items, response: data } }));
      return data;
    } catch (err) {
      toast(err.message || SG.strings.error);
      throw err;
    } finally {
      if (button) { button.removeAttribute('aria-busy'); if (isMain) textEl.textContent = label; }
    }
  }
  SG.addToCart = addToCart;

  function initQuickAdd() {
    on(document, 'click', (e) => {
      const btn = e.target.closest('[data-quick-add]');
      if (!btn) return;
      e.preventDefault();
      const item = { id: Number(btn.dataset.variantId), quantity: 1 };
      if (btn.dataset.sellingPlanId) item.selling_plan = Number(btn.dataset.sellingPlanId);
      addToCart([item], btn, { source: btn.closest('#CartDrawer') ? 'drawer' : 'grid' }).catch(() => {});
    });
  }

  /* ---------- Header: menu, search, sticky height ---------- */
  const Header = {
    setMenu() {},
    init() {
      const nav = $('#SiteNav');
      const toggle = $('[data-menu-toggle]');
      const top = $('#SiteTop');
      const phone = window.matchMedia('(max-width: 800px)');
      const closeMenu = () => setMenu(false);
      const setMenu = (open) => {
        const isOpen = document.body.classList.contains('menu-open');
        if (open === isOpen) return;
        document.body.classList.toggle('menu-open', open);
        if (toggle) toggle.setAttribute('aria-expanded', String(open));
        if (nav) nav.inert = !open && phone.matches;
        setInert([top, $('#Toast')], open);
        if (open) {
          Layers.push(closeMenu);
          if (nav) { const first = nav.querySelector('a, button'); first && first.focus({ preventScroll: true }); }
        } else {
          Layers.remove(closeMenu);
          if (toggle && document.activeElement && nav && nav.contains(document.activeElement)) toggle.focus();
        }
      };
      this.setMenu = setMenu;
      on(toggle, 'click', () => setMenu(!document.body.classList.contains('menu-open')));
      $$('[data-menu-close]').forEach((el) => on(el, 'click', () => setMenu(false)));
      // close the mobile menu after navigating within the same page
      on(nav, 'click', (e) => { if (e.target.closest('a') && phone.matches) setMenu(false); });
      // the off-canvas nav is out of the tab order while closed on phones
      const syncNav = () => { if (nav) nav.inert = phone.matches && !document.body.classList.contains('menu-open'); if (!phone.matches) setMenu(false); };
      syncNav();

      const searchToggle = $('[data-search-toggle]');
      const searchBar = $('#SearchBar');
      const secondary = $('#SecondaryMenu');
      const siteBar = $('#SiteBar');
      // desktop pill bar: the search field is always visible inline, no toggle
      const pillMode = () => !!siteBar && siteBar.dataset.singleRow === 'true' && !phone.matches;
      let searchOpen = false;
      const closeSearch = () => setSearch(false);
      const setSearch = (open) => {
        if (!searchBar) return;
        const pill = pillMode();
        searchOpen = open && !pill;
        searchBar.hidden = pill ? false : !searchOpen;
        document.body.classList.toggle('search-open', searchOpen);
        if (secondary) secondary.hidden = searchOpen; // on phones the search strip replaces the shop menu
        if (searchToggle) {
          searchToggle.setAttribute('aria-expanded', String(searchOpen));
          const icon = $('[data-search-icon]', searchToggle), close = $('[data-search-close]', searchToggle);
          if (icon) icon.hidden = searchOpen; if (close) close.hidden = !searchOpen;
        }
        if (searchOpen) { document.body.classList.remove('header-compact'); Layers.push(closeSearch); const input = $('[data-search-input]', searchBar); input && input.focus(); }
        else { Layers.remove(closeSearch); if (PredictiveSearch.hide) PredictiveSearch.hide(); }
      };
      on(searchToggle, 'click', () => setSearch(!searchOpen));
      setSearch(false);
      // phones: the ticker and the shop strip slide away while scrolling down and come back on scroll up,
      // so only the logo + icons row stays pinned. Layout height never changes (no content jump).
      const announcement = $('.announcement', top);
      let compact = false, lastY = window.scrollY;
      const setCompact = (c) => {
        if (c === compact) return;
        compact = c;
        if (c && announcement) document.documentElement.style.setProperty('--ticker-height', announcement.offsetHeight + 'px');
        document.body.classList.toggle('header-compact', c);
      };
      const onScroll = () => {
        if (!phone.matches || searchOpen || document.body.classList.contains('menu-open')) { setCompact(false); lastY = window.scrollY; return; }
        const y = window.scrollY, dy = y - lastY;
        if (y < 140) setCompact(false);
        else if (dy > 8) setCompact(true);
        else if (dy < -8) setCompact(false);
        lastY = y;
      };
      on(window, 'scroll', onScroll, { passive: true });

      // re-sync only when the phone/desktop breakpoint flips (never on the iOS keyboard resize)
      let wasPhone = phone.matches;
      const breakpointChanged = () => { if (phone.matches === wasPhone) return; wasPhone = phone.matches; syncNav(); setSearch(false); setCompact(false); };
      on(phone, 'change', breakpointChanged);
      on(window, 'resize', debounce(breakpointChanged, 150));

      // publish sticky header height for the sidebar offset
      const setHeight = () => { if (top) document.documentElement.style.setProperty('--header-height', top.offsetHeight + 'px'); };
      setHeight();
      if (window.ResizeObserver && top) new ResizeObserver(debounce(setHeight, 50)).observe(top);
      on(window, 'load', setHeight);

      // mark active link in the secondary strip and scroll it into view on mobile
      const active = $('#SecondaryMenu li.is-active');
      if (active && phone.matches) active.scrollIntoView({ inline: 'center', block: 'nearest' });
    },
  };
  SG.header = Header;

  /* ---------- Predictive search (suggestions, products, collections, pages + recent searches) ---------- */
  const RECENT_SEARCHES = 'sg_recent_searches';
  const PredictiveSearch = {
    hide: null,
    recent() { try { return JSON.parse(localStorage.getItem(RECENT_SEARCHES) || '[]').filter(Boolean).slice(0, 6); } catch (e) { return []; } },
    remember(q) {
      q = (q || '').trim();
      if (q.length < 2) return;
      try { localStorage.setItem(RECENT_SEARCHES, JSON.stringify([q].concat(this.recent().filter((x) => x.toLowerCase() !== q.toLowerCase())).slice(0, 6))); } catch (e) { /* storage unavailable */ }
    },
    init() {
      const form = $('[data-predictive-form]');
      const input = form && $('[data-search-input]', form);
      const results = form && $('[data-predictive-results]', form);
      if (!form || !input || !results) return;
      const strings = SG.strings;
      let controller;
      const esc = (t) => t.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
      input.setAttribute('role', 'combobox');
      input.setAttribute('aria-autocomplete', 'list');
      input.setAttribute('aria-expanded', 'false');
      if (results.id) input.setAttribute('aria-controls', results.id);
      const hide = () => {
        if (results.contains(document.activeElement)) input.focus();
        results.hidden = true;
        input.setAttribute('aria-expanded', 'false');
        Layers.remove(hide);
      };
      const show = () => {
        results.hidden = false;
        input.setAttribute('aria-expanded', 'true');
        Layers.push(hide);
      };
      this.hide = hide;
      const showRecent = () => {
        const list = this.recent();
        if (!list.length) { hide(); results.innerHTML = ''; return; }
        results.innerHTML = '<p class="predictive__heading">' + esc(strings.recentSearches || 'Recent searches') + ' <button type="button" class="predictive__clear" data-clear-recent>' + esc(strings.clearRecent || 'Clear') + '</button></p>'
          + '<div class="predictive__group">' + list.map((q) => '<a class="predictive__query" href="' + SG.routes.search + '?q=' + encodeURIComponent(q) + '">' + esc(q) + '</a>').join('') + '</div>';
        show();
      };
      const run = debounce(async () => {
        const q = input.value.trim();
        if (controller) controller.abort();
        if (q.length < 2) { showRecent(); return; }
        controller = new AbortController();
        try {
          const url = SG.routes.predictiveSearch + '?q=' + encodeURIComponent(q) + '&resources[type]=product,collection,page,article,query&resources[limit]=6&resources[limit_scope]=each&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,variants.title,vendor,tag&section_id=predictive-search';
          const res = await fetch(url, { signal: controller.signal });
          if (!res.ok) throw new Error('predictive search ' + res.status);
          const html = await res.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const inner = doc.querySelector('#PredictiveSearchInner');
          results.innerHTML = inner ? inner.innerHTML : '';
          if (results.innerHTML.trim()) show(); else hide();
        } catch (e) { if (e.name !== 'AbortError') warn('search', e); }
      }, 200);
      on(input, 'input', run);
      on(input, 'focus', () => { if (input.value.trim().length < 2) showRecent(); else if (results.innerHTML.trim()) show(); });
      on(form, 'submit', () => this.remember(input.value));
      on(results, 'click', (e) => {
        const clear = e.target.closest('[data-clear-recent]');
        if (clear) { e.preventDefault(); try { localStorage.removeItem(RECENT_SEARCHES); } catch (err) { /* noop */ } hide(); results.innerHTML = ''; input.focus(); return; }
        const link = e.target.closest('a');
        if (link && link.matches('.predictive__query')) this.remember(link.dataset.predictiveQuery || link.textContent);
        else if (link) this.remember(input.value);
      });
      // keyboard: arrows move through results; Escape is handled by the layer stack
      on(input, 'keydown', (e) => {
        if (e.key === 'ArrowDown' && !results.hidden) { const first = $('a', results); if (first) { e.preventDefault(); first.focus(); } }
      });
      on(results, 'keydown', (e) => {
        const links = $$('a', results);
        const i = links.indexOf(document.activeElement);
        if (e.key === 'ArrowDown' && i < links.length - 1) { e.preventDefault(); links[i + 1].focus(); }
        if (e.key === 'ArrowUp') { e.preventDefault(); if (i > 0) links[i - 1].focus(); else input.focus(); }
      });
      on(document, 'click', (e) => { if (!results.hidden && !form.contains(e.target)) hide(); });
      // the search results page remembers what was searched
      if (document.body.classList.contains('template-search')) { try { const q = new URLSearchParams(location.search).get('q'); if (q) this.remember(q); } catch (e) { /* noop */ } }
    },
  };

  /* ---------- Collection / search filter forms: sort + checkboxes navigate immediately ---------- */
  function initFilterForms() {
    $$('[data-collection-filters]').forEach((form) => {
      const apply = () => {
        const params = new URLSearchParams(new FormData(form));
        const next = new URL(form.getAttribute('action') || window.location.pathname, window.location.origin);
        params.forEach((v, k) => { if (v !== '') next.searchParams.append(k, v); });
        window.location.href = next.toString();
      };
      on(form, 'change', (e) => { if (e.target.matches('[data-sort-by], input[type="checkbox"]')) apply(); });
      on(form, 'submit', (e) => { e.preventDefault(); apply(); });
      // close an open filter panel when clicking elsewhere
      on(document, 'click', (e) => { $$('.filters details[open]', form).forEach((d) => { if (!d.contains(e.target)) d.removeAttribute('open'); }); });
    });
  }

  /* ---------- Tabs (product tabs section; also used on collection pages) ---------- */
  function initTabs(root) {
    $$('[data-tabs]', root || document).forEach((tabs) => {
      if (tabs.dataset.tabsReady) return;
      tabs.dataset.tabsReady = '1';
      const buttons = $$('[role="tab"]', tabs);
      const panels = $$('[role="tabpanel"]', tabs);
      if (!buttons.length) return;
      const select = (i, focus) => {
        buttons.forEach((b, n) => { b.setAttribute('aria-selected', String(n === i)); b.tabIndex = n === i ? 0 : -1; });
        panels.forEach((p, n) => { p.hidden = n !== i; });
        if (focus) buttons[i].focus();
      };
      buttons.forEach((b, n) => {
        on(b, 'click', () => select(n));
        on(b, 'keydown', (e) => {
          const last = buttons.length - 1;
          if (e.key === 'ArrowRight') select(n === last ? 0 : n + 1, true);
          else if (e.key === 'ArrowLeft') select(n === 0 ? last : n - 1, true);
          else if (e.key === 'Home') select(0, true);
          else if (e.key === 'End') select(last, true);
          else return;
          e.preventDefault();
        });
      });
      const current = buttons.findIndex((b) => b.getAttribute('aria-selected') === 'true');
      select(current >= 0 ? current : 0);
    });
  }
  SG.initTabs = initTabs;

  /* ---------- Announcement bar ---------- */
  const Announcement = {
    cleanups: new WeakMap(),
    init(root) {
      $$('[data-announcement]', root || document).forEach((bar) => this.setup(bar));
    },
    setup(bar) {
      const previous = this.cleanups.get(bar);
      if (previous) previous();
      const track = $('[data-announcement-track]', bar);
      const group = $('[data-announcement-group]', bar);
      if (!track || !group) return;
      if (bar.classList.contains('announcement--marquee')) {
        if (reduceMotion()) return;
        let lastKey = '';
        const build = () => {
          const gw = group.getBoundingClientRect().width;
          if (!gw) return;
          const need = Math.min(24, Math.max(1, Math.ceil(bar.clientWidth / gw)));
          const key = need + ':' + Math.round(gw);
          if (key === lastKey) return;
          lastKey = key;
          $$('.announcement__group--clone', track).forEach((c) => c.remove());
          // total copies = 2 * need so translateX(-50%) loops seamlessly
          for (let i = 1; i < need * 2; i++) {
            const clone = group.cloneNode(true);
            clone.classList.add('announcement__group--clone');
            clone.setAttribute('aria-hidden', 'true');
            $$('a, button', clone).forEach((a) => a.setAttribute('tabindex', '-1'));
            track.appendChild(clone);
          }
          const speed = Number(bar.dataset.speed) || 70;
          bar.style.setProperty('--announcement-speed', ((gw * need) / speed).toFixed(2) + 's');
        };
        const onResize = debounce(build, 200);
        build();
        on(window, 'resize', onResize);
        on(window, 'load', build);
        this.cleanups.set(bar, () => { window.removeEventListener('resize', onResize); window.removeEventListener('load', build); });
      } else {
        const items = $$('.announcement__item', group);
        items.forEach((it, n) => it.classList.toggle('is-active', n === 0));
        if (items.length < 2) return;
        let i = 0;
        const delay = (Number(bar.dataset.rotate) || 3) * 1000;
        const timer = setInterval(() => {
          items[i].classList.remove('is-active');
          i = (i + 1) % items.length;
          items[i].classList.add('is-active');
        }, delay);
        this.cleanups.set(bar, () => clearInterval(timer));
      }
    },
  };

  /* ---------- Promo / progress bars ---------- */
  const isBonusLine = (it) => !!(it.properties && it.properties._bonus);
  /* Same rule as the discount Function (and promo-bar.liquid / cart-nudge.liquid): free bonus lines and the
     excluded products — mystery pins/patches bought outright, packs, subscriptions — never count toward a deal. */
  const promoExcluded = new Set((SG.promoExclude || []).map((h) => String(h).trim()).filter(Boolean));
  const countsForPromo = (it) => !isBonusLine(it) && !promoExcluded.has(it.handle);
  const PromoBars = {
    countFor(bar, cart) {
      if (!cart) return 0;
      if (bar.dataset.targetType === 'cart') return cart.total_price;
      return cart.items.reduce((n, it) => (countsForPromo(it) && it.product_type === bar.dataset.target ? n + it.quantity : n), 0);
    },
    refresh(cart) {
      $$('[data-promo]').forEach((bar) => {
        const isCart = bar.dataset.targetType === 'cart';
        const threshold = Number(bar.dataset.threshold) || 0;
        const goal = isCart ? threshold * 100 : threshold;
        const count = this.countFor(bar, cart);
        const cont = bar.dataset.continue === 'true';
        let state = 'empty', remaining = goal - count;
        if (count <= 0) state = 'empty';
        else if (count >= goal) {
          state = 'completed';
          if (cont && goal > 0) { const mod = count % goal; if (mod > 0) { state = 'progressing'; remaining = goal - mod; } }
        } else state = 'progressing';
        // tiers stack (every N → M free): after the first one, say how many are unlocked and what the next step is
        const won = !isCart && goal > 0 ? Math.floor(count / goal) : 0;
        const freeCount = won * (Number(bar.dataset.rewardPerTier) || 0);
        let text = (state === 'progressing' && won > 0 && bar.dataset.progressingNext) || bar.dataset[state] || '';
        const plural = remaining === 1 ? '' : 's';
        text = text.replace(/\{moneyFrom\}/g, formatMoney(Math.max(0, remaining)))
          .replace(/\{cartTotal\}/g, formatMoney(cart ? cart.total_price : 0))
          .replace(/\{from\}/g, String(Math.max(0, remaining)))
          .replace(/\{s\?\}/g, plural)
          .replace(/\{freeCount\}/g, String(freeCount))
          .replace(/\{tierThreshold\}/g, String(threshold));
        let pct = 0;
        if (goal > 0) {
          if (state === 'completed') pct = 100;
          else { let c = count; if (cont && count > goal) c = count % goal; pct = Math.round((c / goal) * 100); }
        }
        bar.style.setProperty('--pct', pct + '%');
        bar.dataset.state = state;
        const t = $('[data-promo-text]', bar);
        if (t && t.innerHTML !== text) t.innerHTML = text;
        bar.hidden = state === 'empty' && bar.dataset.showWhenEmpty === 'false';
      });
    },
    init() {
      on(document, 'cart:updated', (e) => this.refresh(e.detail.cart));
    },
  };

  const PromoRotator = {
    timers: new WeakMap(),
    clear(root) {
      $$('[data-promo-rotator]', root || document).forEach((rot) => { clearInterval(this.timers.get(rot)); this.timers.delete(rot); });
    },
    init(root) {
      $$('[data-promo-rotator]', root || document).forEach((rot) => {
        clearInterval(this.timers.get(rot));
        const bars = $$('.promo-bar', rot).filter((b) => !b.hidden);
        $$('.promo-bar', rot).forEach((b) => b.classList.remove('is-active'));
        if (!bars.length) return;
        let i = 0;
        bars[0].classList.add('is-active');
        if (bars.length < 2) return;
        const ms = (Number(rot.dataset.interval) || 4) * 1000;
        this.timers.set(rot, setInterval(() => {
          bars[i].classList.remove('is-active');
          i = (i + 1) % bars.length;
          bars[i].classList.add('is-active');
        }, ms));
      });
    },
  };

  /* ---------- Bonus items (every N of a type -> M free rewards) ---------- */
  const BonusItems = {
    rules: [],
    running: false,
    pending: false,
    failed: new Set(), // reward variants whose add failed on this page view: no retry loop, no repeated toast
    load() {
      const cfg = $('#BonusItemsConfig');
      if (!cfg) return;
      try { this.rules = JSON.parse(cfg.textContent).filter((r) => r.reward_variant_id && r.threshold > 0); } catch (e) { this.rules = []; }
    },
    rewardVariantIds() {
      return new Set(this.rules.map((r) => Number(r.reward_variant_id)));
    },
    /* The "removed on purpose" guard is per cart (token) and shared between tabs. */
    guardKey(vid, cart) { return 'sg_reward_' + vid + '_' + String(cart.token || '').split('?')[0]; },
    readGuard(key) { try { return Number(localStorage.getItem(key) || 0); } catch (e) { return 0; } },
    writeGuard(key, won) { try { localStorage.setItem(key, String(won)); } catch (e) { /* storage unavailable */ } },
    plan(cart) {
      const adds = [], changes = [];
      this.rules.forEach((rule) => {
        const vid = Number(rule.reward_variant_id);
        const count = cart.items.reduce((n, it) => (countsForPromo(it) && it.product_type === rule.product_type ? n + it.quantity : n), 0);
        const won = Math.floor(count / rule.threshold);
        const target = won * (Number(rule.reward_quantity) || 1);
        // only lines this theme added as free items; a paid line of the same product is left alone
        const lines = cart.items.filter((it) => it.variant_id === vid && isBonusLine(it));
        const current = lines.reduce((n, it) => n + it.quantity, 0);
        const key = this.guardKey(vid, cart);
        let stored = this.readGuard(key);
        if (won < stored) { stored = won; this.writeGuard(key, won); } // tier dropped: arm again for the next climb
        if (current < target) {
          if (stored === won) return; // customer removed them on purpose; wait for a new tier
          if (this.failed.has(vid)) return;
          // the guard is written only once the add has succeeded (see apply)
          adds.push({ id: vid, quantity: target - current, properties: { _bonus: '1' }, message: rule.message, key, won });
        } else if (current > target) {
          // reduce to target: keep the first line, zero the rest
          let remaining = target;
          lines.forEach((it) => { const q = Math.min(remaining, it.quantity); if (q !== it.quantity) changes.push({ id: it.key, quantity: q }); remaining -= q; });
          this.writeGuard(key, won);
        }
      });
      return { adds, changes };
    },
    async apply(cart) {
      if (!this.rules.length || !cart) return;
      // a cart change that lands while free items are being added is re-checked once that finishes
      if (this.running) { this.pending = true; return; }
      const { adds, changes } = this.plan(cart);
      if (!adds.length && !changes.length) return;
      this.running = true;
      this.pending = false;
      try {
        for (const c of changes) await Cart.fetchJson(SG.routes.cartChange + '.js', { method: 'POST', body: JSON.stringify(c) });
        if (adds.length) {
          try {
            await Cart.fetchJson(SG.routes.cartAdd + '.js', { method: 'POST', body: JSON.stringify({ items: adds.map(({ id, quantity, properties }) => ({ id, quantity, properties })) }) });
          } catch (err) { adds.forEach((a) => this.failed.add(a.id)); throw err; }
          adds.forEach((a) => this.writeGuard(a.key, a.won));
          const msg = adds.map((a) => a.message).filter(Boolean)[0] || SG.strings.bonusAdded;
          if (msg) { if (CartDrawer.isOpen()) toast(msg); else viewCartToast(msg); }
        }
        await Cart.refresh();
      } catch (e) { warn('bonus', e); toast(e.message || SG.strings.error); }
      finally {
        this.running = false;
        if (this.pending) {
          this.pending = false;
          Cart.get().then((c) => this.apply(c)).catch((e) => warn('cart', e));
        }
      }
    },
    init() {
      this.load();
      if (!this.rules.length) return;
      on(document, 'cart:updated', (e) => this.apply(e.detail.cart));
      // check once on load when the cart has something in it (it may have changed elsewhere)
      if (SG.cartCount > 0) Cart.get().then((cart) => this.apply(cart)).catch((e) => warn('cart', e));
    },
  };
  SG.bonus = BonusItems;

  /* ---------- Recommendations (cart drawer) ---------- */
  const Recommendations = {
    cache: {},
    async cart() {
      const box = $('[data-cart-recs]');
      if (!box || !box.dataset.productId || box.dataset.loaded || !CartDrawer.isOpen()) return;
      if ($('[data-cart-nudge]')) { box.hidden = true; return; } // the tier nudge already fills this role
      const id = box.dataset.productId;
      box.dataset.loaded = '1';
      const fill = (html) => {
        const h = document.createElement('h3');
        h.textContent = box.dataset.heading || '';
        box.replaceChildren(h);
        box.insertAdjacentHTML('beforeend', html);
        // nothing that is already in the cart (the free mystery pins included)
        const inCart = new Set($$('.cart-item[data-product-id]', CartDrawer.el).map((row) => row.dataset.productId));
        $$('.recommendations__card[data-product-id]', box).forEach((card) => { if (inCart.has(card.dataset.productId)) card.remove(); });
        box.hidden = !$('.recommendations__card', box);
      };
      if (this.cache[id]) { fill(this.cache[id]); return; }
      try {
        for (const intent of ['complementary', 'related']) {
          const url = SG.routes.productRecommendations + '?section_id=product-recommendations&product_id=' + id + '&limit=6&intent=' + intent;
          const res = await fetch(url);
          if (!res.ok) continue;
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          const track = doc.querySelector('.recommendations__track');
          if (track && track.children.length) { this.cache[id] = track.outerHTML; fill(track.outerHTML); return; }
        }
      } catch (e) { warn('recommendations', e); }
    },
  };

  /* ---------- Misc ---------- */
  function initScrollTop() {
    const btn = $('#ScrollTop');
    if (!btn) return;
    const update = () => btn.classList.toggle('is-visible', window.scrollY > 400);
    on(window, 'scroll', debounce(update, 80), { passive: true });
    on(btn, 'click', () => window.scrollTo({ top: 0, behavior: reduceMotion() ? 'auto' : 'smooth' }));
    update();
  }

  /* A blank or non-numeric quantity (the field cleared to type a new number) is not "remove this line". */
  const typedQuantity = (input) => {
    const n = parseInt(input.value, 10);
    if (Number.isNaN(n)) { input.value = input.defaultValue; return null; }
    return Math.max(0, n);
  };
  SG.typedQuantity = typedQuantity;

  function initCartPage() {
    const main = $('#MainCart');
    if (!main) return;
    const busy = () => main.getAttribute('aria-busy') === 'true';
    on(main, 'click', async (e) => {
      const btn = e.target.closest('[data-qty-change], [data-remove]');
      if (!btn) return;
      const row = btn.closest('[data-key]');
      if (!row) return;
      e.preventDefault();
      if (busy()) return; // one change at a time: rapid taps would read a stale quantity
      const input = row.querySelector('[data-qty-input]');
      const qty = btn.matches('[data-remove]') ? 0 : Math.max(0, (parseInt(input.value, 10) || 0) + parseInt(btn.dataset.qtyChange, 10));
      main.setAttribute('aria-busy', 'true');
      try { await Cart.change({ id: row.dataset.key, quantity: qty }); } catch (err) { toast(err.message); }
      finally { main.removeAttribute('aria-busy'); }
    });
    on(main, 'change', async (e) => {
      const input = e.target.closest('[data-qty-input]');
      if (!input) return;
      const qty = typedQuantity(input);
      if (qty === null) return;
      const row = input.closest('[data-key]');
      main.setAttribute('aria-busy', 'true');
      try { await Cart.change({ id: row.dataset.key, quantity: qty }); } catch (err) { toast(err.message); }
      finally { main.removeAttribute('aria-busy'); }
    });
    // the order note saves as it's typed, so a quantity change (which re-renders the page) or an
    // express checkout (Shop Pay, PayPal…) never drops it
    const saveNote = debounce((value) => {
      Cart.fetchJson(SG.routes.cartUpdate + '.js', { method: 'POST', body: JSON.stringify({ note: value }) }).catch((err) => warn('note', err));
    }, 400);
    on(main, 'input', (e) => { if (e.target.matches('textarea[name="note"]')) saveNote(e.target.value); });
  }

  /* ---------- Product cards: second image on first hover (desktop only) ---------- */
  function initHoverImages() {
    if (!window.matchMedia('(hover: hover)').matches) return;
    const load = (link) => {
      const img = link && link.querySelector('img.hover-image[data-src]');
      if (!img) return;
      img.srcset = img.dataset.srcset || '';
      img.sizes = img.dataset.sizes || '';
      img.src = img.dataset.src;
      delete img.dataset.src;
    };
    on(document, 'mouseover', (e) => load(e.target.closest('.has-hover-image')), { passive: true });
    on(document, 'focusin', (e) => load(e.target.closest('.has-hover-image')));
  }

  /* ---------- Email signup (sidebar / footer): Klaviyo client API, Shopify customer form as fallback ---------- */
  function initNewsletter() {
    $$('[data-newsletter-form]').forEach((form) => {
      on(form, 'submit', async (e) => {
        const cfg = SG.klaviyo || {};
        if (!cfg.company || !cfg.list || !window.fetch) return; // no list configured: post Shopify's form
        e.preventDefault();
        const input = $('input[type="email"]', form);
        const button = $('button[type="submit"]', form);
        const email = input ? input.value.trim() : '';
        if (!email || (button && button.disabled)) return;
        if (button) button.disabled = true;
        try {
          const res = await fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(cfg.company), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
            body: JSON.stringify({ data: { type: 'subscription', attributes: { custom_source: form.dataset.source || 'Theme signup', profile: { data: { type: 'profile', attributes: { email } } } }, relationships: { list: { data: { type: 'list', id: cfg.list } } } } }),
          });
          if (!res.ok) throw new Error('klaviyo ' + res.status);
          const row = $('.signup__row', form), ok = $('.signup__success', form);
          if (row) row.hidden = true;
          if (ok) ok.hidden = false;
          try { window._learnq = window._learnq || []; window._learnq.push(['identify', { $email: email }]); } catch (err) { /* noop */ }
        } catch (err) {
          warn('newsletter', err);
          if (button) button.disabled = false;
          form.submit(); // Shopify's customer form still records the signup
        }
      });
    });
  }

  /* ---------- Collection page: collapsible description + bonus tier strip ---------- */
  function initCollectionExtras() {
    $$('[data-collapsible]').forEach((wrap) => {
      const body = $('[data-collapsible-body]', wrap);
      const toggle = $('[data-collapsible-toggle]', wrap);
      if (!body || !toggle) return;
      const more = toggle.textContent;
      const less = SG.strings.readLess || 'less';
      const check = () => {
        if (wrap.classList.contains('is-open')) return;
        toggle.hidden = !(body.scrollHeight > body.clientHeight + 2);
      };
      on(toggle, 'click', () => {
        const open = wrap.classList.toggle('is-open');
        toggle.setAttribute('aria-expanded', String(open));
        toggle.textContent = open ? less : more;
      });
      check();
      on(window, 'resize', debounce(check, 150));
      on(window, 'load', check);
    });
    if ($('[data-collection-strip]')) document.body.classList.add('has-collection-strip');
  }

  /* ---------- boot ---------- */
  function boot() {
    // each module is isolated: one failure (an old browser, an app rewriting the DOM) can't stop the rest
    const run = (name, fn) => { try { fn(); } catch (e) { warn('init ' + name, e); } };
    run('drawer', () => CartDrawer.init());
    run('quickadd', initQuickAdd);
    run('header', () => Header.init());
    run('search', () => PredictiveSearch.init());
    run('announcement', () => Announcement.init());
    run('promobars', () => PromoBars.init());
    run('rotator', () => PromoRotator.init());
    run('bonus', () => BonusItems.init());
    run('tabs', () => initTabs());
    run('scrolltop', initScrollTop);
    run('cartpage', initCartPage);
    run('hover', initHoverImages);
    run('collection', initCollectionExtras);
    run('filters', initFilterForms);
    run('newsletter', initNewsletter);
    // back/forward cache: the cart may have changed in checkout
    on(window, 'pageshow', (e) => { if (e.persisted) Cart.refresh().catch((err) => warn('pageshow', err)); });
    // Shopify theme editor: re-init only what the reloaded section contains
    on(document, 'shopify:section:load', (e) => {
      const root = e.target;
      Announcement.init(root);
      PromoRotator.init(root);
      initTabs(root);
      if (root.querySelector('#CartDrawer')) { CartDrawer.el = $('#CartDrawer'); CartDrawer.bind(); }
      if (root.querySelector('#BonusItemsConfig')) BonusItems.load();
    });
  }
  if (document.readyState === 'loading') on(document, 'DOMContentLoaded', boot); else boot();
})();
